import React from "react";
import { CircularProgress, SxProps } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  SpinnerContainer,
  LabelContainer,
  CustomButton,
} from "./Button.styles";
import { Variants, SizeStyles, GradientStyles } from "./Button.variants";

type GradientStylesReturnType = ReturnType<typeof GradientStyles>;
type GradientStylesType = keyof GradientStylesReturnType;
export interface CustomVariantButtonProps {
  variant?: "primary" | "secondary" | "secondaryLight" | "secondaryDark" | "tertiary" | "success" | "destructive" | "destructiveDark" | "secondaryGray" | "ghost" | "linkButtonDisabled";
  size?: "xs" | "sm" | "md" | "lg";
  type?: "button" | "submit";
  disabledStateStyling?: "dark" | "lowOpacity";
  gradientStyle?: GradientStylesType;
  disabled?: boolean;
  full?: boolean;
  loading?: boolean;
  leftIcon?: React.ReactElement | null;
  rightIcon?: React.ReactElement | null;
  children?: React.ReactNode;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  sx?: SxProps;
  href?: string;
}

export const Button = ({
  variant = "primary",
  size = "md",
  type = "button",
  gradientStyle = "gradient1",
  disabledStateStyling = "dark",
  disabled = false,
  full = false,
  leftIcon = null,
  rightIcon = null,
  loading,
  children,
  sx,
  href,
  ...rest
}: CustomVariantButtonProps) => {
  const theme = useTheme();

  const variantStylesFn = Variants(theme);
  const sizeStylesFn = SizeStyles(theme);
  const gradientStylesFn = GradientStyles(theme);

  const conditionalGradient =
    variant === "primary" ? gradientStylesFn[gradientStyle] : {};

  return (
    <CustomButton
      disabled={disabled || loading}
      fullWidth={full}
      type={type}
      sx={{
        ...variantStylesFn[variant],
        ...sizeStylesFn[size],
        ...conditionalGradient,
        ...sx,
      }}
      {...rest}
      className={`
        ${loading ? "is-loading" : ""}
        ${disabledStateStyling === "lowOpacity" ? "lowOpacity-disabled" : ""}
      `}
      href={href}
    >
      <LabelContainer className="label-wrapper">
        {leftIcon}
        <span>{children}</span>
        {rightIcon}
      </LabelContainer>

      {loading && (
        <SpinnerContainer>
          <CircularProgress size={20} color="inherit" />
        </SpinnerContainer>
      )}
    </CustomButton>
  );
};

export interface ButtonProps extends React.ComponentProps<typeof Button> {
  as?: React.ElementType;
}

Button.displayName = "Button";
